import { FC, memo, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Checkbox, Col, Divider, Flex, Row, Space, Tag, Typography } from 'antd';
import {
    TbArrowRight,
    TbBuilding,
    TbCalendarSearch,
    TbChevronDown,
    TbChevronRight,
    TbCopy,
    TbFileText,
    TbLayoutGrid,
    TbLayoutRows,
    TbNews,
    TbProgressCheck,
    TbTransactionDollar,
    TbUserSearch,
    TbWorldDollar,
    TbX,
} from 'react-icons/tb';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useDismissibleSelection } from '@/hooks/useDismissibleSelection';
import { useRecentSearches, type RecentSearchResourceType, type RecentSearchScope } from '@/hooks/useRecentSearches';
import type { SearchResultsFooterReporter } from '@/layouts/02-Authenticated/layout/03-Content/routes/04-Search/SearchResultsFooterState';
import { useSearchResultsFooterReporter } from '@/layouts/02-Authenticated/layout/03-Content/routes/04-Search/useSearchResultsFooterReporter';
import { VirtualizedList as List, PlatformAlert, unwrapSearchFilters, type SearchFilterChangeHandler } from '@/shared';
import ConnectedFileDocumentDrawer from '@/shared/components/attachments/ConnectedFileDocumentDrawer';
import {
    DEFAULT_AVAILABLE_COMPANY_RESULT_COLUMNS,
    DEFAULT_COMPANY_RESULT_COLUMNS,
} from '@/shared/components/company-search/columns';
import CompanyResultGroupRows from '@/shared/components/company-search/CompanyResultGroupRows';
import { CompanySearchResult, groupCompanySearchResults } from '@/shared/components/company-search/utils';
import ExecutiveDetailDrawer from '@/shared/components/lists/ExecutiveInsidersView/ExecutiveDetailDrawer';
import SourceDocumentReader from '@/shared/components/lists/SourceDocumentReader';
import { toast } from '@/shared/components/toasts/PlatformToast';

import RecentSearchHistory from '../../../../components/RecentSearchHistory';
import SearchEmptyState from '../../../../components/SearchEmptyState';
import SearchResultsSkeleton, {
    SEARCH_RESULTS_SECTION_SKELETON_ROWS,
    type SearchSkeletonDomain,
} from '../../../../components/SearchResultsSkeleton';
import { getCompanyResultColumnFilter } from '../../../02-Companies/01-CompaniesSearch/helpers';
import CompanyActionsRow, {
    type CompanyActionResult,
} from '../../../02-Companies/01-CompaniesSearch/layout/03-Content/views/01-Results/layout/01-ActionsRow';
import CompanyListHeader from '../../../02-Companies/01-CompaniesSearch/layout/03-Content/views/01-Results/layout/02-ResultsList/components/01-ListHeader';
import CompanyListItem from '../../../02-Companies/01-CompaniesSearch/layout/03-Content/views/01-Results/layout/02-ResultsList/components/02-CompanyListItem';
import type { AllSearchDomain, SearchPersonContext, UnifiedSearchResult } from '../../useAllSearch';
import DomainResultsTable, { SearchResultRowsSkeleton } from '../DomainResultsTable';

interface SearchResultsListProps {
    criteriaActive?: boolean;
    domains: readonly AllSearchDomain[];
    emptyDescription?: string;
    error?: string;
    filters?: unknown;
    groupRelatedListings?: boolean;
    handleChange?: SearchFilterChangeHandler;
    loading: boolean;
    loadingDomains?: readonly AllSearchDomain[];
    pageSize?: number;
    pending?: boolean;
    query: string;
    reportResultsFooter?: SearchResultsFooterReporter;
    results: UnifiedSearchResult[];
    showRecentSearches?: boolean;
}

const NOOP_RESULTS_FOOTER_REPORTER: SearchResultsFooterReporter = () => undefined;
const RESULTS_SUMMARY_STICKY_HEIGHT = '3.0625rem';
const RESULTS_SECTION_STICKY_HEIGHT = '2.0625rem';
const RESULTS_COLUMN_STICKY_TOP = `calc(${RESULTS_SUMMARY_STICKY_HEIGHT} + ${RESULTS_SECTION_STICKY_HEIGHT})`;

const getSearchCoverageAlertCopy = (error: string) => {
    const unavailable = error === 'Search providers are unavailable.';

    return {
        description: unavailable
            ? 'We could not retrieve results from the selected sources. Try again shortly or adjust your search criteria.'
            : 'Search remains available, but coverage may be incomplete while some sources recover. Review the results below or try again shortly for a more complete view.',
        title: unavailable
            ? 'Search results are temporarily unavailable.'
            : 'Some results may be temporarily unavailable.',
    };
};

const DOMAIN_PRESENTATION: Record<AllSearchDomain, { icon: ReactNode; label: string }> = {
    companies: { icon: <TbBuilding />, label: 'Companies, Funds & Indices' },
    documents: { icon: <TbFileText />, label: 'Documents & Filings' },
    economic: { icon: <TbTransactionDollar />, label: 'Economic Data' },
    events: { icon: <TbCalendarSearch />, label: 'Events' },
    news: { icon: <TbNews />, label: 'News' },
    people: { icon: <TbUserSearch />, label: 'People' },
    trade: { icon: <TbWorldDollar />, label: 'Global Trade' },
    workspace: { icon: <TbLayoutGrid />, label: 'Workspace' },
};

const RECENT_RESOURCE_TYPE_BY_DOMAIN: Record<AllSearchDomain, RecentSearchResourceType> = {
    companies: 'company',
    documents: 'document',
    economic: 'economic',
    events: 'event',
    news: 'news',
    people: 'person',
    trade: 'trade',
    workspace: 'workspace',
};

const SearchResultsList: FC<SearchResultsListProps> = ({
    criteriaActive = false,
    domains,
    emptyDescription,
    error,
    filters,
    groupRelatedListings = true,
    handleChange,
    loading,
    loadingDomains = [],
    pageSize = 25,
    pending = false,
    query,
    reportResultsFooter = NOOP_RESULTS_FOOTER_REPORTER,
    results,
    showRecentSearches = true,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [selectedPersonResult, setSelectedPersonResult] = useState<
        UnifiedSearchResult & { person: SearchPersonContext }
    >();
    const [selectedDocumentResult, setSelectedDocumentResult] = useState<UnifiedSearchResult>();
    const [personDrawerOpen, setPersonDrawerOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [collapsedDomains, setCollapsedDomains] = useState<Set<AllSearchDomain>>(new Set());
    const [companyListColumns, setCompanyListColumns] = useState<string[]>([...DEFAULT_COMPANY_RESULT_COLUMNS]);
    const recentSearchScope: RecentSearchScope = domains.length === 1 ? domains[0] : 'all';
    const recentHistory = useRecentSearches(10, recentSearchScope);
    const { addRecentResource, addSearch } = recentHistory;
    const coverageAlertCopy = error ? getSearchCoverageAlertCopy(error) : undefined;
    const legacyFilters = useMemo(() => (handleChange ? unwrapSearchFilters(filters) : {}), [filters, handleChange]);
    const handleApplyCompanyColumnFilter = useCallback(
        (column: string, result: unknown) => {
            const columnFilter = getCompanyResultColumnFilter(column, result, legacyFilters);

            if (columnFilter) {
                handleChange?.(columnFilter.path, columnFilter.value);
            }
        },
        [handleChange, legacyFilters],
    );
    const multipleDomains = domains.length > 1;
    const pendingDomains = useMemo(
        () => (loading && loadingDomains.length === 0 ? domains : loadingDomains),
        [domains, loading, loadingDomains],
    );
    const effectivePage = Math.min(page, Math.max(1, Math.ceil(results.length / pageSize)));
    const resultsByDomain = useMemo(() => {
        const grouped = new Map<AllSearchDomain, UnifiedSearchResult[]>();
        results.forEach((result) => grouped.set(result.domain, [...(grouped.get(result.domain) ?? []), result]));

        return grouped;
    }, [results]);
    const groupedResults = useMemo(
        () =>
            domains
                .map((domain) => {
                    const domainResults = resultsByDomain.get(domain) ?? [];
                    const visibleDomainResults = multipleDomains
                        ? domainResults.slice(0, Math.min(pageSize, 10))
                        : domainResults.slice((effectivePage - 1) * pageSize, effectivePage * pageSize);

                    return [domain, visibleDomainResults] as const;
                })
                .filter(([domain, domainResults]) => domainResults.length > 0 || pendingDomains.includes(domain)),
        [domains, effectivePage, multipleDomains, pageSize, pendingDomains, resultsByDomain],
    );
    const visibleResults = useMemo(
        () => groupedResults.flatMap(([, domainResults]) => domainResults),
        [groupedResults],
    );
    const validSelectedIds = useMemo(() => {
        const validIds = new Set(results.map((result) => result.id));

        return new Set(Array.from(selectedIds).filter((id) => validIds.has(id)));
    }, [results, selectedIds]);
    const selectedResults = useMemo(
        () => results.filter((result) => validSelectedIds.has(result.id)),
        [results, validSelectedIds],
    );
    const selectedCompanyActionResults = useMemo<CompanyActionResult[]>(
        () =>
            selectedResults
                .filter((result) => result.domain === 'companies' && Boolean(result.company))
                .map((result) => ({ ...result.company, selectionId: result.id })),
        [selectedResults],
    );
    const companyActionsAvailable =
        selectedResults.length > 0 && selectedCompanyActionResults.length === selectedResults.length;
    const personResults = useMemo(
        () =>
            results.filter((result): result is UnifiedSearchResult & { person: SearchPersonContext } =>
                Boolean(result.person),
            ),
        [results],
    );
    const personExecutives = useMemo(() => personResults.map((result) => result.person.executive), [personResults]);
    const allVisibleSelected =
        visibleResults.length > 0 && visibleResults.every((result) => validSelectedIds.has(result.id));
    const someVisibleSelected = visibleResults.some((result) => validSelectedIds.has(result.id));
    const emptyStateScope = domains.length === 1 ? domains[0] : 'all';
    const allSectionsCollapsed =
        groupedResults.length > 0 && groupedResults.every(([domain]) => collapsedDomains.has(domain));
    const toggleDomain = (domain: AllSearchDomain) => {
        setCollapsedDomains((current) => {
            const next = new Set(current);
            if (next.has(domain)) {
                next.delete(domain);
            } else {
                next.add(domain);
            }
            return next;
        });
    };
    const toggleAllDomains = () => {
        setCollapsedDomains(allSectionsCollapsed ? new Set() : new Set(groupedResults.map(([domain]) => domain)));
    };
    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    useDismissibleSelection({ active: validSelectedIds.size > 0, onDismiss: clearSelection });

    useSearchResultsFooterReporter({
        active: Boolean(query.trim() || criteriaActive || pending),
        loading,
        onPageChange: multipleDomains ? undefined : setPage,
        page: multipleDomains ? 1 : effectivePage,
        pageSize: multipleDomains ? Math.max(1, results.length) : pageSize,
        report: reportResultsFooter,
        total: results.length,
    });

    useEffect(() => {
        if (query.trim() && !loading && results.length > 0) {
            addSearch(query);
        }
    }, [addSearch, loading, query, results.length]);

    const openUrl = (result: UnifiedSearchResult) => {
        if (!result.url) {
            return;
        }
        if (result.external) {
            window.open(result.url, '_blank', 'noopener,noreferrer');
        } else {
            navigate(result.url);
        }
    };

    const openPerson = (result: UnifiedSearchResult & { person: SearchPersonContext }) => {
        setSelectedPersonResult(result);
        setPersonDrawerOpen(true);
    };

    const openDocument = (result: UnifiedSearchResult) => setSelectedDocumentResult(result);

    const rememberResult = (result: UnifiedSearchResult) => {
        const requiresSearchReplay = Boolean(result.document || result.person);
        const replaySearchParams = new URLSearchParams(location.search);
        replaySearchParams.set('q', query);
        replaySearchParams.set('openResult', result.id);
        const path = requiresSearchReplay ? `/search/${result.domain}?${replaySearchParams.toString()}` : result.url;

        if (!path) {
            return;
        }

        addRecentResource({
            exchange: result.exchange,
            external: requiresSearchReplay ? false : result.external,
            id: result.id,
            logoUrl: result.logoUrl,
            name: result.title,
            path,
            resourceType: RECENT_RESOURCE_TYPE_BY_DOMAIN[result.domain],
            scope: result.domain,
            symbol: result.symbol,
            website: result.website,
        });
    };

    const openResult = (result: UnifiedSearchResult) => {
        rememberResult(result);

        if (result.document) {
            openDocument(result);
            return;
        }
        if (result.person) {
            openPerson({ ...result, person: result.person });
            return;
        }

        openUrl(result);
    };

    useEffect(() => {
        if (loading) {
            return;
        }

        const searchParams = new URLSearchParams(location.search);
        const resultId = searchParams.get('openResult');
        const result = resultId ? results.find((candidate) => candidate.id === resultId) : undefined;

        if (!result) {
            return;
        }

        searchParams.delete('openResult');
        navigate({ pathname: location.pathname, search: searchParams.toString() }, { replace: true });

        window.setTimeout(() => {
            if (result.document) {
                setSelectedDocumentResult(result);
            } else if (result.person) {
                setSelectedPersonResult({ ...result, person: result.person });
                setPersonDrawerOpen(true);
            }
        }, 0);
    }, [loading, location.pathname, location.search, navigate, results]);

    const copyResultLink = async (result: UnifiedSearchResult) => {
        if (!result.url) {
            return;
        }
        try {
            const link = result.external ? result.url : new URL(result.url, window.location.origin).toString();
            await navigator.clipboard.writeText(link);
            toast.success('Link copied.');
        } catch (copyError) {
            captureException(copyError, { tags: { action: 'copy-search-result-link' } });
            toast.error('Unable to copy this link.');
        }
    };

    const copySelectedResultLinks = async () => {
        try {
            const links = selectedResults.flatMap((result) => {
                if (!result.url) {
                    return [];
                }

                return [result.external ? result.url : new URL(result.url, window.location.origin).toString()];
            });

            await navigator.clipboard.writeText(links.join('\n'));
            toast.success(`${links.length.toLocaleString()} result link${links.length === 1 ? '' : 's'} copied.`);
        } catch (copyError) {
            captureException(copyError, { tags: { action: 'copy-selected-search-result-links' } });
            toast.error('Unable to copy the selected result links.');
        }
    };

    const selectVisibleResults = (checked: boolean) => {
        setSelectedIds((current) => {
            const next = new Set(current);
            visibleResults.forEach((result) => (checked ? next.add(result.id) : next.delete(result.id)));
            return next;
        });
    };

    if (!query.trim() && !criteriaActive && !pending) {
        return showRecentSearches ? (
            <RecentSearchHistory
                groups={recentHistory.recentGroups}
                onClear={recentHistory.clearRecentSearches}
                queries={recentHistory.recentQueries}
                resources={recentHistory.recentResources}
                scope={recentSearchScope}
            />
        ) : (
            <SearchEmptyState height='100%' mode='initial' scope={emptyStateScope} />
        );
    }

    if (!loading && results.length === 0) {
        return (
            <SearchEmptyState description={emptyDescription} height='100%' mode='no-results' scope={emptyStateScope} />
        );
    }

    if (loading && results.length === 0 && domains.length === 1) {
        const skeletonDomain: SearchSkeletonDomain = domains.length === 1 ? domains[0] : 'companies';

        return (
            <div style={{ minWidth: 0 }}>
                {coverageAlertCopy ? (
                    <div data-testid='all-search-coverage-alert' style={{ padding: '0.5rem 0.5rem 0' }}>
                        <PlatformAlert
                            description={coverageAlertCopy.description}
                            status='warning'
                            title={coverageAlertCopy.title}
                        />
                    </div>
                ) : null}
                <SearchResultsSkeleton domain={skeletonDomain} />
            </div>
        );
    }

    return (
        <div style={{ minWidth: 0 }}>
            {coverageAlertCopy ? (
                <div data-testid='all-search-coverage-alert' style={{ padding: '0.5rem 0.5rem 0' }}>
                    <PlatformAlert
                        description={coverageAlertCopy.description}
                        status='warning'
                        title={coverageAlertCopy.title}
                    />
                </div>
            ) : null}
            <div
                data-testid='all-search-results-summary'
                style={{
                    backgroundColor: token.colorBgContainer,
                    borderBottom: `1px solid ${token.colorBorder}`,
                    padding: '0.5rem',
                    position: 'sticky',
                    top: 0,
                    zIndex: 10,
                }}
            >
                <Row
                    align='middle'
                    style={{
                        border: `1px solid ${token.colorBorder}`,
                        borderRadius: token.borderRadius,
                        columnGap: '0.5rem',
                        minHeight: '2rem',
                        padding: '0.25rem',
                    }}
                    wrap={false}
                >
                    <Col flex='none' style={{ marginLeft: '0.25rem' }}>
                        <span data-selection-control='true' style={{ display: 'inline-flex' }}>
                            <Checkbox
                                aria-label='Select all visible search results'
                                checked={allVisibleSelected}
                                indeterminate={!allVisibleSelected && someVisibleSelected}
                                onChange={(event) => selectVisibleResults(event.target.checked)}
                            />
                        </span>
                    </Col>
                    <Col flex='none'>
                        <Typography.Text style={{ color: token.colorTextSecondary }}>
                            {loading && results.length === 0
                                ? 'Searching across sources…'
                                : `${results.length.toLocaleString()} result${results.length === 1 ? '' : 's'}`}
                        </Typography.Text>
                    </Col>
                    {validSelectedIds.size > 0 && companyActionsAvailable ? (
                        <>
                            <Col flex='none'>
                                <Divider
                                    style={{ borderColor: token.colorBorder, height: '1rem', margin: '0 0.125rem' }}
                                    type='vertical'
                                />
                            </Col>
                            <Col flex='none' style={{ minWidth: 0 }}>
                                <CompanyActionsRow
                                    results={selectedCompanyActionResults}
                                    resultsSelected={validSelectedIds}
                                    setCheckAll={() => undefined}
                                    setIndeterminate={() => undefined}
                                    setResultsSelected={setSelectedIds}
                                />
                            </Col>
                        </>
                    ) : validSelectedIds.size > 0 ? (
                        <>
                            <Col flex='none'>
                                <Divider
                                    style={{ borderColor: token.colorBorder, height: '1rem', margin: '0 0.125rem' }}
                                    type='vertical'
                                />
                            </Col>
                            <Col flex='none'>
                                <span data-selection-control='true' style={{ display: 'inline-flex' }}>
                                    <Space.Compact size='small' style={{ borderRadius: token.borderRadius }}>
                                        <Button
                                            icon={<TbProgressCheck style={{ verticalAlign: '-0.125rem' }} />}
                                            style={{ pointerEvents: 'none' }}
                                            type='dashed'
                                        >
                                            <small>
                                                {validSelectedIds.size.toLocaleString()} Result
                                                {validSelectedIds.size === 1 ? '' : 's'} Selected
                                            </small>
                                        </Button>
                                        <Button
                                            aria-label='Clear selected search results'
                                            icon={<TbX style={{ color: token.colorTextTertiary }} />}
                                            onClick={clearSelection}
                                            type='dashed'
                                        />
                                    </Space.Compact>
                                </span>
                            </Col>
                            <Col flex='none'>
                                <Divider
                                    style={{ borderColor: token.colorBorder, height: '1rem', margin: '0 0.125rem' }}
                                    type='vertical'
                                />
                            </Col>
                            <Col flex='none'>
                                <span data-selection-control='true' style={{ display: 'inline-flex' }}>
                                    <Space.Compact size='small' style={{ borderRadius: token.borderRadius }}>
                                        <Button
                                            aria-label='Copy selected result links'
                                            disabled={!selectedResults.some((result) => Boolean(result.url))}
                                            icon={<TbCopy size='0.875rem' />}
                                            onClick={() => void copySelectedResultLinks()}
                                            size='small'
                                            type='dashed'
                                        />
                                    </Space.Compact>
                                </span>
                            </Col>
                        </>
                    ) : null}
                    {multipleDomains ? (
                        <>
                            <Col flex='none'>
                                <Divider
                                    style={{ borderColor: token.colorBorder, height: '1rem', margin: '0 0.125rem' }}
                                    type='vertical'
                                />
                            </Col>
                            <Col flex='none'>
                                <Space.Compact size='small' style={{ borderRadius: token.borderRadius }}>
                                    <Button icon={<TbLayoutRows />} onClick={toggleAllDomains} type='dashed'>
                                        {allSectionsCollapsed ? 'Expand all' : 'Collapse all'}
                                    </Button>
                                </Space.Compact>
                            </Col>
                        </>
                    ) : null}
                    <Col flex='auto' />
                </Row>
            </div>
            {groupedResults.map(([domain, domainResults]) => {
                const presentation = DOMAIN_PRESENTATION[domain];
                const domainLoading = pendingDomains.includes(domain);
                const collapsed = collapsedDomains.has(domain);
                const nonCompanyDomain = domain === 'companies' ? undefined : domain;
                const totalDomainResults = resultsByDomain.get(domain)?.length ?? 0;
                const companyResults = domainResults.filter(
                    (result): result is UnifiedSearchResult & { company: Record<string, unknown> } =>
                        Boolean(result.company),
                );
                const rawCompanyResults = companyResults.map((result) => result.company);
                const companyResultByRawResult = new Map(
                    companyResults.map((result) => [result.company as CompanySearchResult, result]),
                );
                const companyGroups = groupRelatedListings
                    ? groupCompanySearchResults(rawCompanyResults)
                    : rawCompanyResults.map((company) => ({
                          alternatives: [],
                          id: String(company.entityId ?? company.symbol ?? company.id ?? company.name),
                          label: String(company.companyName ?? company.name ?? company.symbol ?? 'Company'),
                          primary: company,
                      }));
                const allCompaniesSelected =
                    companyResults.length > 0 && companyResults.every((result) => validSelectedIds.has(result.id));
                const someCompaniesSelected = companyResults.some((result) => validSelectedIds.has(result.id));
                const selectCompanies = (checked: boolean) => {
                    setSelectedIds((current) => {
                        const next = new Set(current);
                        companyResults.forEach((result) => (checked ? next.add(result.id) : next.delete(result.id)));
                        return next;
                    });
                };

                return (
                    <div key={domain} style={{ position: 'relative' }}>
                        <Flex
                            align='center'
                            data-testid={`all-search-domain-${domain}`}
                            gap={6}
                            style={{
                                backgroundColor: token.colorBgContainer,
                                borderBottom: `1px solid ${token.colorBorderSecondary}`,
                                color: token.colorTextSecondary,
                                minHeight: '2rem',
                                padding: '0.25rem 0.5rem',
                                position: 'sticky',
                                top: RESULTS_SUMMARY_STICKY_HEIGHT,
                                zIndex: 9,
                            }}
                        >
                            <Button
                                aria-expanded={!collapsed}
                                aria-label={`${collapsed ? 'Expand' : 'Collapse'} ${presentation.label}`}
                                icon={collapsed ? <TbChevronRight /> : <TbChevronDown />}
                                onClick={() => toggleDomain(domain)}
                                size='small'
                                style={{ alignItems: 'center', display: 'inline-flex' }}
                                type='text'
                            >
                                <span style={{ alignItems: 'center', display: 'inline-flex', gap: '0.375rem' }}>
                                    {presentation.icon}
                                    <Typography.Text strong>{presentation.label}</Typography.Text>
                                </span>
                            </Button>
                            <Tag bordered={false}>{totalDomainResults.toLocaleString()}</Tag>
                            {multipleDomains && totalDomainResults > domainResults.length ? (
                                <Button
                                    icon={<TbArrowRight />}
                                    iconPosition='end'
                                    onClick={() =>
                                        navigate({
                                            pathname: `/search/${domain}`,
                                            search: window.location.search,
                                        })
                                    }
                                    size='small'
                                    style={{ marginInlineStart: 'auto' }}
                                    type='dashed'
                                >
                                    View all
                                </Button>
                            ) : null}
                        </Flex>
                        {!collapsed && domainLoading && domainResults.length === 0 ? (
                            <SearchResultsSkeleton domain={domain} rows={SEARCH_RESULTS_SECTION_SKELETON_ROWS} />
                        ) : !collapsed && domain === 'companies' ? (
                            <List
                                dataSource={companyGroups}
                                footer={
                                    domainLoading ? (
                                        <SearchResultRowsSkeleton
                                            columnCount={companyListColumns.length}
                                            domain='companies'
                                        />
                                    ) : undefined
                                }
                                getItemKey={(group) => group.id}
                                headerStyle={{
                                    backgroundColor: token.colorBgContainer,
                                    borderBottom: `1px solid ${token.colorBorder}`,
                                    boxSizing: 'border-box',
                                    paddingBlock: '0.5rem',
                                    paddingInline: '0.5rem',
                                    position: 'sticky',
                                    top: RESULTS_COLUMN_STICKY_TOP,
                                    zIndex: 8,
                                }}
                                header={
                                    <CompanyListHeader
                                        availableColumns={[...DEFAULT_AVAILABLE_COMPANY_RESULT_COLUMNS]}
                                        checkAll={allCompaniesSelected}
                                        handleCheckAllChange={selectCompanies}
                                        indeterminate={!allCompaniesSelected && someCompaniesSelected}
                                        listColumns={companyListColumns}
                                        results={rawCompanyResults}
                                        resultsSelected={validSelectedIds}
                                        setListViewColumns={setCompanyListColumns}
                                        setResultsSelected={setSelectedIds}
                                    />
                                }
                                itemLayout='vertical'
                                renderItem={(group) => (
                                    <CompanyResultGroupRows
                                        group={group}
                                        renderResult={(company) => {
                                            const result = companyResultByRawResult.get(company);

                                            if (!result) {
                                                return null;
                                            }

                                            return (
                                                <CompanyListItem
                                                    data={company}
                                                    key={result.id}
                                                    listColumns={companyListColumns}
                                                    onApplyColumnFilter={
                                                        handleChange ? handleApplyCompanyColumnFilter : undefined
                                                    }
                                                    onSelectedChange={(checked) => {
                                                        setSelectedIds((current) => {
                                                            const next = new Set(current);
                                                            if (checked) {
                                                                next.add(result.id);
                                                            } else {
                                                                next.delete(result.id);
                                                            }
                                                            return next;
                                                        });
                                                    }}
                                                    resultsCount={companyResults.length}
                                                    resultsSelected={validSelectedIds}
                                                    selected={validSelectedIds.has(result.id)}
                                                    selectionId={result.id}
                                                    setCheckAll={() => undefined}
                                                    setIndeterminate={() => undefined}
                                                    setResultsSelected={setSelectedIds}
                                                />
                                            );
                                        }}
                                    />
                                )}
                                size='small'
                                style={{ backgroundColor: token.colorBgContainer }}
                            />
                        ) : !collapsed && nonCompanyDomain ? (
                            <DomainResultsTable
                                domain={nonCompanyDomain}
                                loading={domainLoading}
                                onCopy={(result) => void copyResultLink(result)}
                                onOpen={openResult}
                                onSelectedChange={(id, checked) => {
                                    setSelectedIds((current) => {
                                        const next = new Set(current);
                                        if (checked) {
                                            next.add(id);
                                        } else {
                                            next.delete(id);
                                        }
                                        return next;
                                    });
                                }}
                                presentationIcon={presentation.icon}
                                results={domainResults}
                                selectedIds={validSelectedIds}
                                stickyTop={RESULTS_COLUMN_STICKY_TOP}
                            />
                        ) : null}
                    </div>
                );
            })}
            {!loading && results.length > 0 ? (
                <Divider plain style={{ margin: '2.5rem 0' }}>
                    <small style={{ color: token.colorTextTertiary }}>End of Search Results</small>
                </Divider>
            ) : null}
            <ExecutiveDetailDrawer
                company={selectedPersonResult?.person.company ?? { name: 'Company unavailable' }}
                executive={selectedPersonResult?.person.executive}
                executives={personExecutives}
                onExecutiveChange={(executive) => {
                    const nextResult = personResults.find((result) => result.person.executive.id === executive?.id);

                    if (nextResult) {
                        setSelectedPersonResult(nextResult);
                    }
                }}
                onOpenChange={setPersonDrawerOpen}
                open={personDrawerOpen}
                profileKind={selectedPersonResult?.person.isExecutive ? 'executive' : 'person'}
                showCompensation={Boolean(selectedPersonResult?.person.isExecutive)}
            />
            {selectedDocumentResult?.document?.connectedFile ? (
                <ConnectedFileDocumentDrawer
                    key={selectedDocumentResult.id}
                    {...selectedDocumentResult.document.connectedFile}
                    onClose={() => setSelectedDocumentResult(undefined)}
                />
            ) : (
                <SourceDocumentReader
                    navigationAction={selectedDocumentResult?.document?.navigationAction}
                    onClose={() => setSelectedDocumentResult(undefined)}
                    source={selectedDocumentResult?.document?.source}
                />
            )}
        </div>
    );
};

export default memo(SearchResultsList);
